import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { Badge } from '../../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  UserCircle,
  Upload,
  X,
  Eye,
  Loader2,
} from 'lucide-react'
import * as util from '../../../common/Util'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import BadgeTemplateEndpoints, { BadgeTemplate as BadgeTemplateType } from '../../../endpoints/BadgeTemplateEndpoints'
import { BadgePropertyOverrides } from '../../common/BadgePropertyOverrides'
import { BadgePreviewDialog } from './BadgePreviewDialog'
import { useEventContext } from '../EventContext'
import {
  IdentityRoleRequest,
  IdentityRoleRequestFromIdentityRole,
} from '../../../requests/EventRequest'
import { DefaultBadgeConfigurationRequest } from '../../../requests/BadgeConfigurationRequest'
import {
  IdentityRole,
  AccreditationLevel,
  Event as EventModel,
} from '../../../model/Event'
import { toast } from 'sonner'

// Exported so the setup wizard's slim role form starts from the same defaults
// (notably the shared DefaultBadgeConfigurationRequest) and can't drift.
export const defaultRoleRequest: IdentityRoleRequest = {
  internalName: '',
  badgeName: '',
  defaultAccreditationLevelId: null,
  schedulable: false,
  hasAppAccess: false,
  badgeOverridden: false,
  badgeConfiguration: DefaultBadgeConfigurationRequest(),
}

export const Roles: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { event, setEvent, canEdit } = useEventContext()
  const [roles, setRoles] = useState<IdentityRole[]>([])
  const [accreditationLevels, setAccreditationLevels] = useState<AccreditationLevel[]>([])
  const [saving, setSaving] = useState(false)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<IdentityRole | null>(null)

  const [formData, setFormData] = useState<IdentityRoleRequest>(defaultRoleRequest)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [badgeTemplates, setBadgeTemplates] = useState<BadgeTemplateType[]>([])

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean
    blob: Blob | null
    filename: string
  }>({ open: false, blob: null, filename: 'badge-preview.pdf' })

  const fallbackTemplates: BadgeTemplateType[] = [
    { id: 1, name: 'Vertical (Default)', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'PORTRAIT', size: 'STANDARD', width: 210, height: 325, createdBy: null, createdAt: '', updatedAt: '' },
    { id: 2, name: 'Horizontal', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'LANDSCAPE', size: 'STANDARD', width: 325, height: 210, createdBy: null, createdAt: '', updatedAt: '' },
  ]

  const clientId = event?.client?.id

  useEffect(() => {
    if (clientId == null) return
    BadgeTemplateEndpoints.getTemplates(true, undefined, clientId)
      .then((data) => setBadgeTemplates(data.length > 0 ? data : fallbackTemplates))
      .catch(() => setBadgeTemplates(fallbackTemplates))
  }, [clientId])

  useEffect(() => {
    if (event) {
      setRoles(event.roles || [])
      setAccreditationLevels(event.accreditationLevels || [])
    }
  }, [event])

  const validateForm = (data: IdentityRoleRequest): Record<string, string> => {
    const errors: Record<string, string> = {}
    if (!data.internalName.trim()) {
      errors.internalName = 'Name is required'
    }
    if (!data.badgeName.trim()) {
      errors.badgeName = 'Badge name is required'
    }
    return errors
  }

  const handleCreate = async () => {
    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      setSaving(true)
      const result = await EventEndpoints.addEventRole(parseInt(eventId!), formData)
      setEvent(result.parent)
      setRoles(result.parent.roles || [])
      toast.success('Role created successfully')
      setCreateDialogOpen(false)
      setFormData(defaultRoleRequest)
      setFormErrors({})
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create role'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async () => {
    if (!selectedRole) return

    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setEditDialogOpen(false)
    setSaving(true)

    try {
      await EventEndpoints.editEventRole(selectedRole.id, formData)
      const updatedEvent = await EventEndpoints.getEvent(parseInt(eventId!))
      setEvent(updatedEvent)
      setRoles(updatedEvent.roles || [])
      setAccreditationLevels(updatedEvent.accreditationLevels || [])
      toast.success('Role updated successfully')
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update role'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
      setSelectedRole(null)
      setFormData(defaultRoleRequest)
      setFormErrors({})
    }
  }

  const handleDelete = async () => {
    if (!selectedRole) return

    setDeleteDialogOpen(false)
    setSaving(true)

    try {
      const updatedEvent = await EventEndpoints.deleteEventRole(selectedRole.id)
      setEvent(updatedEvent)
      setRoles(updatedEvent.roles || [])
      toast.success('Role deleted successfully')
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to delete role'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
      setSelectedRole(null)
    }
  }

  const openCreateDialog = () => {
    setSelectedRole(null)
    setFormData(defaultRoleRequest)
    setFormErrors({})
    setCreateDialogOpen(true)
  }

  const openEditDialog = (role: IdentityRole) => {
    setSelectedRole(role)
    setFormData(IdentityRoleRequestFromIdentityRole(role))
    setFormErrors({})
    setEditDialogOpen(true)
  }

  const openDeleteDialog = (role: IdentityRole) => {
    setSelectedRole(role)
    setDeleteDialogOpen(true)
  }

  // Declared above the loading early-return: the event arrives asynchronously,
  // so a hook after that return would change hook order on the render it lands.
  const selectedBadgeTemplate = useMemo(() => {
    if (!formData.badgeOverridden) return null
    const tid = formData.badgeConfiguration?.templateId || 1
    return badgeTemplates.find((t) => t.id === tid) || null
  }, [formData.badgeOverridden, formData.badgeConfiguration?.templateId, badgeTemplates])

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const sortedRoles = [...roles].sort((a, b) => a.internalName.localeCompare(b.internalName))

  const rolePropertyOverrides: Record<string, string> = formData.badgeConfiguration?.propertyOverrides || {}

  // Layout-based templates render straight from the posted overrides, so the
  // preview can show unsaved edits. SVG templates are rendered server-side from
  // the persisted configuration, so those previews reflect the last save — and
  // for a role that isn't created yet (or doesn't override), the event default,
  // which is exactly what the role will print.
  const livePreviewTemplate =
    formData.badgeOverridden && selectedBadgeTemplate?.isLayoutBased
      ? selectedBadgeTemplate
      : null

  const previewHint = livePreviewTemplate
    ? 'Preview includes the overrides and logo above, saved or not.'
    : selectedRole
      ? 'This template renders server-side, so the preview shows the last saved settings.'
      : 'Shows the badge this role will print once created.'

  const handlePreviewBadge = async () => {
    if (!eventId) return

    setPreviewLoading(true)
    try {
      const response = livePreviewTemplate
        ? await BadgeTemplateEndpoints.previewTemplate(
            livePreviewTemplate.id,
            rolePropertyOverrides,
            formData.badgeConfiguration?.base64LogoImage,
            formData.badgeConfiguration?.logoImageContentType,
            {
              eventId: parseInt(eventId),
              roleBadgeName: formData.badgeName,
              accreditationLevelId: formData.defaultAccreditationLevelId,
              badgeName: formData.badgeConfiguration?.badgeName,
            }
          )
        : await EventEndpoints.getBadgePreview(parseInt(eventId), selectedRole?.id)

      const blob = response instanceof Blob ? response : await response.blob()
      const name = (formData.internalName.trim() || 'role').replace(/[^\w-]+/g, '-')
      setPreviewDialog({ open: true, blob, filename: `badge-preview-${name}.pdf` })
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message || error?.message || 'Failed to generate badge preview'
      toast.error(errorMsg)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleRoleImageUpload = (fieldName: 'logoImage' | 'backImage') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    util.FileToBase64(file, (base64: string) => {
      setFormData((prev) => ({
        ...prev,
        badgeConfiguration: {
          ...prev.badgeConfiguration!,
          ...(fieldName === 'logoImage'
            ? { base64LogoImage: base64, logoImageContentType: file.type }
            : { base64BackImage: base64, backImageContentType: file.type }),
        },
      }))
    })
  }

  const clearRoleImage = (fieldName: 'logoImage' | 'backImage') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setFormData((prev) => ({
      ...prev,
      badgeConfiguration: {
        ...prev.badgeConfiguration!,
        ...(fieldName === 'logoImage'
          ? { base64LogoImage: null, logoImageContentType: null }
          : { base64BackImage: null, backImageContentType: null }),
      },
    }))
  }

  const roleImageUpload = (
    fieldName: 'logoImage' | 'backImage',
    currentImage: string | null | undefined,
    currentType: string | null | undefined
  ) => (
    <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-3 hover:border-gray-400 cursor-pointer">
      <input
        type="file"
        accept="image/*"
        onChange={handleRoleImageUpload(fieldName)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      {currentImage ? (
        <>
          <img
            src={`data:${currentType};base64,${currentImage}`}
            alt="Preview"
            className="max-w-full max-h-24 mx-auto rounded"
          />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-1 right-1 h-6 w-6"
            onClick={clearRoleImage(fieldName)}
          >
            <X className="h-3 w-3" />
          </Button>
        </>
      ) : (
        <div className="text-center py-2">
          <Upload className="mx-auto h-6 w-6 text-gray-400 mb-1" />
          <p className="text-xs text-gray-600">Click or drag image</p>
        </div>
      )}
    </div>
  )

  const roleFormFields = (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="internalName" className="text-right">Name *</Label>
        <div className="col-span-3">
          <Input
            id="internalName"
            value={formData.internalName}
            onChange={(e) => setFormData((prev) => ({ ...prev, internalName: e.target.value }))}
            placeholder="e.g., Security, Volunteer, Staff"
            className={formErrors.internalName ? 'border-red-500' : ''}
          />
          {formErrors.internalName && (
            <p className="text-sm text-red-500 mt-1">{formErrors.internalName}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="badgeName" className="text-right">Badge Name *</Label>
        <div className="col-span-3">
          <Input
            id="badgeName"
            value={formData.badgeName}
            onChange={(e) => setFormData((prev) => ({ ...prev, badgeName: e.target.value }))}
            placeholder="Text printed on badge"
            className={formErrors.badgeName ? 'border-red-500' : ''}
          />
          {formErrors.badgeName && (
            <p className="text-sm text-red-500 mt-1">{formErrors.badgeName}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 items-center gap-4">
        <Label className="text-right">Default Accreditation</Label>
        <div className="col-span-3">
          <Select
            value={formData.defaultAccreditationLevelId?.toString() || 'none'}
            onValueChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                defaultAccreditationLevelId: value === 'none' ? null : parseInt(value),
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {accreditationLevels.map((level) => (
                <SelectItem key={level.id} value={level.id.toString()}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-4 items-start gap-4">
        <Label className="text-right pt-1">Options</Label>
        <div className="col-span-3 space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="schedulable"
              checked={formData.schedulable}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, schedulable: checked === true }))
              }
            />
            <Label htmlFor="schedulable" className="font-normal">Schedulable</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hasAppAccess"
              checked={formData.hasAppAccess}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, hasAppAccess: checked === true }))
              }
            />
            <Label htmlFor="hasAppAccess" className="font-normal">AuthentiGATE App Access</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="badgeOverridden"
              checked={formData.badgeOverridden}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, badgeOverridden: checked === true }))
              }
            />
            <Label htmlFor="badgeOverridden" className="font-normal">Override badge template</Label>
          </div>
        </div>
      </div>
      {formData.badgeOverridden && (
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="configBadgeName" className="text-right">Badge Name</Label>
          <div className="col-span-3">
            <Input
              id="configBadgeName"
              value={formData.badgeConfiguration?.badgeName || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  badgeConfiguration: {
                    ...prev.badgeConfiguration!,
                    badgeName: e.target.value,
                  },
                }))
              }
              placeholder="Title shown on the badge (e.g., event name)"
            />
            <p className="text-xs text-muted-foreground mt-1">Override the badge title (resolves the `badgeName` text variable on the badge).</p>
          </div>
        </div>
      )}
      {formData.badgeOverridden && (
        <div className="grid grid-cols-4 items-center gap-4">
          <Label className="text-right">Badge Template</Label>
          <div className="col-span-3">
            <Select
              value={formData.badgeConfiguration?.templateId?.toString() || '1'}
              onValueChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  badgeConfiguration: {
                    ...prev.badgeConfiguration!,
                    templateId: parseInt(v),
                  },
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {badgeTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id.toString()}>
                    {t.name} {t.isLayoutBased ? '(Visual)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">This role will use a different badge template than the event default.</p>
          </div>
        </div>
      )}
      {formData.badgeOverridden && (
        <div className="grid grid-cols-4 items-start gap-4">
          <Label className="text-right pt-2">Images</Label>
          <div className="col-span-3 grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Logo</Label>
              {roleImageUpload(
                'logoImage',
                formData.badgeConfiguration?.base64LogoImage,
                formData.badgeConfiguration?.logoImageContentType
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Back</Label>
              {roleImageUpload(
                'backImage',
                formData.badgeConfiguration?.base64BackImage,
                formData.badgeConfiguration?.backImageContentType
              )}
            </div>
          </div>
        </div>
      )}
      {formData.badgeOverridden && selectedBadgeTemplate?.isLayoutBased && (
        <div className="grid grid-cols-4 items-start gap-4">
          <Label className="text-right pt-1">Overrides</Label>
          <div className="col-span-3">
            <BadgePropertyOverrides
              template={selectedBadgeTemplate}
              overrides={rolePropertyOverrides}
              onChange={(next) => {
                setFormData((prev) => ({
                  ...prev,
                  badgeConfiguration: {
                    ...prev.badgeConfiguration!,
                    propertyOverrides: Object.keys(next).length > 0 ? next : null,
                  },
                }))
              }}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-4 items-center gap-4 border-t pt-4">
        <div className="text-right">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviewBadge}
            disabled={previewLoading}
          >
            {previewLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
        </div>
        <p className="col-span-3 text-xs text-muted-foreground">{previewHint}</p>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Roles
              </CardTitle>
              <CardDescription>
                Manage roles for {event?.name || 'this event'}
              </CardDescription>
            </div>
            <Button onClick={openCreateDialog} disabled={!canEdit}>
              <Plus className="h-4 w-4 mr-2" />
              Add Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <div className="text-center py-8">
              <UserCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No roles</h3>
              <p className="text-muted-foreground mb-4">
                Create your first role to start assigning identities.
              </p>
              <Button onClick={openCreateDialog} disabled={!canEdit}>
                <Plus className="h-4 w-4 mr-2" />
                Create Role
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Badge Name</TableHead>
                  <TableHead className="text-center">Schedulable</TableHead>
                  <TableHead className="text-center">App Access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="text-muted-foreground">{role.id}</TableCell>
                    <TableCell className="font-medium">{role.internalName}</TableCell>
                    <TableCell>{role.badgeName}</TableCell>
                    <TableCell className="text-center">
                      {role.schedulable ? (
                        <Badge variant="default" className="text-xs">Yes</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {role.hasAppAccess ? (
                        <Badge variant="default" className="text-xs">Yes</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={role.state === 'REFERENCED' ? 'default' : 'secondary'}>
                        {role.state === 'REFERENCED' ? 'In Use' : 'Unused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(role)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {role.state === 'UNREFERENCED' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => openDeleteDialog(role)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
            <DialogDescription>Add a new role for this event.</DialogDescription>
          </DialogHeader>
          {roleFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false)
                setFormData(defaultRoleRequest)
                setFormErrors({})
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setEditDialogOpen(false)
            setFormData(defaultRoleRequest)
            setFormErrors({})
            setSelectedRole(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>Update the role details.</DialogDescription>
          </DialogHeader>
          {roleFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setFormData(defaultRoleRequest)
                setFormErrors({})
                setSelectedRole(null)
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setDeleteDialogOpen(false)
            setSelectedRole(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRole?.internalName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteDialogOpen(false); setSelectedRole(null) }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BadgePreviewDialog
        open={previewDialog.open}
        onOpenChange={(open) => setPreviewDialog((prev) => ({ ...prev, open }))}
        pdfBlob={previewDialog.blob}
        filename={previewDialog.filename}
      />
    </div>
  )
}
